import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, Truck, Banknote, ShieldCheck, ChevronLeft, Lock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { useApp } from '@/state/AppContext';
import { inr } from '@/lib/format';
import { createOrder, createShopifyCheckout } from '@/lib/api';
import { FreeShippingBar } from '@/components/FreeShippingBar';
import { Footer } from '@/components/Footer';
import { imgSrc } from '@/lib/img';

const CheckoutPage = () => {
  const { cart, subtotal, shipping, total, clearCart, config } = useApp();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const shopifyEnabled = !!config?.shopify_enabled;

  // Mock flow state (only used when Shopify is disabled — dev/preview scenarios)
  const [form, setForm] = useState({
    full_name: '', phone: '', email: '',
    line1: '', line2: '', city: '', state: '', pincode: '',
    notes: '',
  });
  const [payment, setPayment] = useState('mock_card');
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validateMock = () => {
    const reqs = ['full_name', 'phone', 'email', 'line1', 'city', 'state', 'pincode'];
    for (const k of reqs) {
      if (!form[k] || form[k].trim() === '') { toast.error(`Please fill ${k.replace('_', ' ')}`); return false; }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error('Enter a valid email'); return false; }
    if (!/^\d{10,}$/.test(form.phone.replace(/\D/g, ''))) { toast.error('Enter a valid phone number'); return false; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error('Enter a valid 6-digit pincode'); return false; }
    return true;
  };

  // Empty cart guard
  if (cart.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10" data-testid="checkout-page">
        <p className="text-sm text-muted-foreground">Your cart is empty.</p>
        <Button asChild className="mt-3 h-11 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white">
          <Link to="/shop">Shop now</Link>
        </Button>
      </div>
    );
  }

  // ----- Shopify-enabled: single "Place order" → Shopify redirect -----
  const placeOrderShopify = async () => {
    setSubmitting(true);
    try {
      const items = cart.map((c) => ({
        product_id: c.product_id,
        product_slug: c.product_slug,
        name: c.name,
        qty: c.qty,
        shopify_variant_id: c.shopify_variant_id || null,
        customization: c.customization || null,
        pack: c.pack || null,
        customised: !!(c.customised || c.customization),
      }));
      const res = await createShopifyCheckout(items);
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      toast.error('Checkout unavailable — please try again in a moment.');
    } catch (e) {
      toast.error('Could not start checkout', { description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Mock flow (dev only, kept for local testing without Shopify) -----
  const placeOrderMock = async () => {
    if (!validateMock()) return;
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          product_id: c.product_id, product_slug: c.product_slug, name: c.name,
          image: c.image, price: c.price, qty: c.qty, variant: c.variant,
          customization: c.customization || null,
        })),
        address: { ...form },
        subtotal, shipping, total,
        payment_method: payment,
        notes: form.notes,
      };
      const res = await createOrder(payload);
      toast.success('Order placed!', { description: `Order ID: ${res.order_id}` });
      clearCart();
      localStorage.setItem('bb_last_email', form.email);
      navigate(`/order-confirmation/${res.order_id}`);
    } catch (e) {
      console.error(e);
      toast.error('Could not place order', { description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const placeOrder = shopifyEnabled ? placeOrderShopify : placeOrderMock;
  const ctaLabel = submitting
    ? (shopifyEnabled ? 'Redirecting…' : 'Placing…')
    : `Place order · ${inr(total)}`;

  return (
    <div className="pb-32 md:pb-16" data-testid="checkout-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-3">
        <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="w-4 h-4" /> Back to cart
        </Link>
      </div>
      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-3">
        <h1 className="font-display text-2xl md:text-3xl font-bold">Checkout</h1>
        <p className="text-sm text-muted-foreground">
          {shopifyEnabled
            ? 'Review your order, then complete payment securely on the next step.'
            : 'Guest checkout · No account needed'}
        </p>
      </section>

      {/* -----------------------------------------------------------
        SHOPIFY-ENABLED PATH: Slim review page with ONE Place order button.
        Address + payment are collected by Shopify in the next step,
        so nothing here is redundant.
      ----------------------------------------------------------- */}
      {shopifyEnabled && (
        <section className="max-w-6xl mx-auto px-4 md:px-6 mt-4 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {/* Line items review */}
            <div className="rounded-2xl bg-white border border-border p-4 bb-shadow-soft" data-testid="checkout-review">
              <div className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--bb-pink)]" /> Your order
              </div>
              <div className="divide-y divide-border">
                {cart.map((c) => (
                  <div key={c.line_id} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                    <img
                      src={imgSrc(c.customization?.preview_data_url || c.image, 'thumb')}
                      alt={c.name}
                      className="w-16 h-16 rounded-lg object-cover bg-[var(--bb-sky-soft)] shrink-0"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold line-clamp-1">{c.name}</div>
                      {c.customised && <div className="text-[11px] uppercase tracking-wide text-[var(--bb-pink-deep)] font-nunito-bold mt-0.5">Customised</div>}
                      <div className="text-xs text-muted-foreground mt-0.5">Qty {c.qty}</div>
                    </div>
                    <div className="text-sm font-semibold whitespace-nowrap">{inr(c.price * c.qty)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Handoff info panel */}
            <div className="rounded-2xl border border-border p-4 bg-[var(--bb-sky-soft)]/50" data-testid="checkout-handoff-info">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-[var(--bb-darkblue)] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-nunito-black text-[var(--bb-darkblue)]">Delivery & payment on the next step</div>
                  <div className="text-xs text-[var(--bb-darkgrey)] mt-1 leading-relaxed">
                    You'll enter your address and pay with card, UPI, netbanking, or wallets on a secure Shopify page. Auto-fill kicks in if you've shopped with any Shopify store before.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order summary + Place order */}
          <aside className="rounded-2xl bg-white border border-border p-4 h-fit bb-shadow-soft md:sticky md:top-20" data-testid="checkout-summary">
            <FreeShippingBar />
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span>{shipping === 0 ? <span className="text-[var(--bb-pink-deep)] font-semibold">FREE</span> : inr(shipping)}</span>
              </div>
              <div className="border-t border-border pt-2 mt-1 flex justify-between text-base font-bold">
                <span>Total</span><span data-testid="checkout-total">{inr(total)}</span>
              </div>
            </div>
            <Button
              onClick={placeOrder}
              disabled={submitting}
              className="w-full h-12 rounded-xl mt-4 bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white bb-shadow-pink hidden md:flex font-nunito-black"
              data-testid="place-order-button"
            >
              {ctaLabel}
            </Button>
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <ShieldCheck className="w-3.5 h-3.5 text-[var(--bb-pink)]" /> Secured by Shopify
            </div>
          </aside>
        </section>
      )}

      {/* -----------------------------------------------------------
        MOCK PATH: only when Shopify is disabled (dev/preview).
        Full address form + mock payment radios.
      ----------------------------------------------------------- */}
      {!shopifyEnabled && (
        <section className="max-w-6xl mx-auto px-4 md:px-6 mt-4 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {/* Contact + Address */}
            <div className="rounded-2xl bg-white border border-border p-4 bb-shadow-soft">
              <div className="font-semibold mb-3">Delivery details</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Full name</Label>
                  <Input value={form.full_name} onChange={update('full_name')} className="h-11 rounded-xl mt-1" data-testid="checkout-name" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={update('phone')} placeholder="10-digit mobile" className="h-11 rounded-xl mt-1" data-testid="checkout-phone" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={update('email')} className="h-11 rounded-xl mt-1" data-testid="checkout-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Address line 1</Label>
                  <Input value={form.line1} onChange={update('line1')} className="h-11 rounded-xl mt-1" data-testid="checkout-line1" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Address line 2 (optional)</Label>
                  <Input value={form.line2} onChange={update('line2')} className="h-11 rounded-xl mt-1" data-testid="checkout-line2" />
                </div>
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={form.city} onChange={update('city')} className="h-11 rounded-xl mt-1" data-testid="checkout-city" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={form.state} onChange={update('state')} className="h-11 rounded-xl mt-1" data-testid="checkout-state" />
                </div>
                <div>
                  <Label className="text-xs">Pincode</Label>
                  <Input value={form.pincode} onChange={update('pincode')} className="h-11 rounded-xl mt-1" data-testid="checkout-pincode" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">Order notes (optional)</Label>
                <Textarea value={form.notes} onChange={update('notes')} placeholder="Gift message, delivery instructions…" className="mt-1 rounded-xl" rows={3} data-testid="checkout-notes" />
              </div>
            </div>

            {/* Payment - MOCKED */}
            <div className="rounded-2xl bg-white border border-border p-4 bb-shadow-soft">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">Payment</div>
                <span className="text-[10px] uppercase tracking-wide font-bold bg-[var(--bb-yellow)]/30 text-[var(--bb-choco)] px-2 py-0.5 rounded-full">DEMO / MOCKED</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">No real payment is processed in this demo. Your order will be placed and saved.</p>
              <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2" data-testid="checkout-payment">
                <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-[var(--bb-sky-soft)]">
                  <RadioGroupItem value="mock_card" id="pay-card" />
                  <CreditCard className="w-5 h-5 text-[var(--bb-pink-deep)]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Card (mock)</div>
                    <div className="text-[11px] text-muted-foreground">Skips real processing.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-[var(--bb-sky-soft)]">
                  <RadioGroupItem value="mock_upi" id="pay-upi" />
                  <Banknote className="w-5 h-5 text-[var(--bb-pink-deep)]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">UPI (mock)</div>
                    <div className="text-[11px] text-muted-foreground">Demo — no real charge.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-border p-3 cursor-pointer hover:bg-[var(--bb-sky-soft)]">
                  <RadioGroupItem value="cod" id="pay-cod" />
                  <Truck className="w-5 h-5 text-[var(--bb-pink-deep)]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Cash on Delivery</div>
                    <div className="text-[11px] text-muted-foreground">Pay your delivery partner.</div>
                  </div>
                </label>
              </RadioGroup>
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="w-4 h-4 text-[var(--bb-pink)]" /> Your details stay private. We only use them to deliver your order.
              </div>
            </div>
          </div>

          {/* Summary */}
          <aside className="rounded-2xl bg-white border border-border p-4 h-fit bb-shadow-soft md:sticky md:top-20">
            <FreeShippingBar />
            <div className="mt-4 space-y-2 max-h-60 overflow-y-auto pr-1">
              {cart.map((c) => (
                <div key={c.line_id} className="flex gap-2">
                  <img src={imgSrc(c.customization?.preview_data_url || c.image, 'avatar')} alt={c.name} className="w-12 h-12 rounded-md object-cover bg-[var(--bb-sky-soft)]" loading="lazy" decoding="async" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold line-clamp-1">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground">Qty {c.qty}</div>
                  </div>
                  <div className="text-xs font-semibold">{inr(c.price * c.qty)}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{shipping === 0 ? <span className="text-[var(--bb-pink-deep)] font-semibold">FREE</span> : inr(shipping)}</span></div>
              <div className="border-t border-border pt-2 mt-1 flex justify-between text-base font-bold"><span>Total</span><span>{inr(total)}</span></div>
            </div>
            <Button
              onClick={placeOrder}
              disabled={submitting}
              className="w-full h-12 rounded-xl mt-4 bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white bb-shadow-pink hidden md:flex"
              data-testid="place-order-button"
            >
              {ctaLabel}
            </Button>
          </aside>
        </section>
      )}

      <Footer />

      {/* Sticky place order bar (mobile) — used by BOTH flows */}
      <div
        className="fixed left-0 right-0 z-40 bg-white border-t border-border md:hidden"
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-base font-bold">{inr(total)}</div>
          </div>
          <Button
            onClick={placeOrder}
            disabled={submitting}
            className="h-11 px-5 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black"
            data-testid="place-order-button-mobile"
          >
            {submitting ? (shopifyEnabled ? 'Redirecting…' : 'Placing…') : 'Place order'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
